// Environment configuration for Vite
// This file handles environment variables in a type-safe way

interface EnvironmentConfig {
  API_ROOT_PATH: string;
  API_PREFIX: string;
  RECAPTCHA_SITE_KEY: string;
  RECAPTCHA_SITE_KEY_V2: string;
  APP_NAME: string;
  LOGO_IMAGE_URL: string;
  BANNER_IMAGE_URL: string;
  OLE_ROUTE_PATH: string;
  MAGIC_LINK_ENABLED: boolean;
  GOOGLE_AUTH_ENABLED: boolean;
  MICROSOFT_AUTH_ENABLED: boolean;
  APPLE_AUTH_ENABLED: boolean;
  PROPERTY_ID_KEY: string;
  FLOOR_PLAN_ID_KEY: string;
  PROPERTY_LOGO_URL_KEY: string;
  PROPERTY_BANNER_IMAGE_URL_KEY: string;
}

// Helper function to parse boolean environment variables
const parseBoolean = (value: string | undefined): boolean => {
  return value?.toLowerCase() === 'true';
};

// Environment configuration with fallbacks
export const env: EnvironmentConfig = {
  API_ROOT_PATH: import.meta.env?.VITE_API_ROOT_PATH || '/onlineleasingstudent/api/',
  API_PREFIX: import.meta.env?.VITE_API_PREFIX || '/onlineleasingstudent/api/',
  RECAPTCHA_SITE_KEY: import.meta.env?.VITE_RECAPTCHA_SITE_KEY || '',
  RECAPTCHA_SITE_KEY_V2: import.meta.env?.VITE_RECAPTCHA_SITE_KEY_V2 || '',
  APP_NAME: import.meta.env?.VITE_APP_NAME || 'RentCafe OLE',
  LOGO_IMAGE_URL: import.meta.env?.VITE_LOGO_IMAGE_URL || '/onlineleasingstudent/content3/assets/images/rentcafeole_login.png',
  BANNER_IMAGE_URL: import.meta.env?.VITE_BANNER_IMAGE_URL || '/onlineleasingstudent/content3/assets/images/banner.jpg',
  OLE_ROUTE_PATH: import.meta.env?.VITE_OLE_ROUTE_PATH || '/onlineleasingstudent/content3/',
  MAGIC_LINK_ENABLED: parseBoolean(import.meta.env?.VITE_MAGIC_LINK_ENABLED),
  GOOGLE_AUTH_ENABLED: parseBoolean(import.meta.env?.VITE_GOOGLE_AUTH_ENABLED),
  MICROSOFT_AUTH_ENABLED: parseBoolean(import.meta.env?.VITE_MICROSOFT_AUTH_ENABLED),
  APPLE_AUTH_ENABLED: parseBoolean(import.meta.env?.VITE_APPLE_AUTH_ENABLED),
  PROPERTY_ID_KEY: import.meta.env?.VITE_PROPERTY_ID_KEY || 'PropertyId',
  FLOOR_PLAN_ID_KEY: import.meta.env?.VITE_FLOOR_PLAN_ID_KEY || 'FloorPlanID',
  PROPERTY_LOGO_URL_KEY: import.meta.env?.VITE_PROPERTY_LOGO_URL_KEY || 'PropertyLogoUrl',
  PROPERTY_BANNER_IMAGE_URL_KEY: import.meta.env?.VITE_PROPERTY_BANNER_IMAGE_URL_KEY || 'PropertyBannerImageUrl',
};

// Build Environment Configuration for Lazy Loading & Code Splitting
export type AppEnvironment = 'development' | 'qa' | 'production'

export interface BuildEnvironmentConfig {
  env: AppEnvironment
  isDev: boolean
  isQA: boolean
  isProd: boolean
  apiBaseUrl: string
  enableDevtools: boolean
  logLevel: 'debug' | 'info' | 'warn' | 'error'
  chunkSizeWarningLimit: number
}

// Get environment from build-time constants or fallback to import.meta.env
const getAppEnv = (): AppEnvironment => {
  // Check build-time constants first (set by Vite defineConfig)
  if (typeof __APP_ENV__ !== 'undefined') {
    return __APP_ENV__ as AppEnvironment
  }
  
  // Fallback to runtime environment detection
  if (import.meta.env.MODE === 'qa') return 'qa'
  if (import.meta.env.MODE === 'production') return 'production'
  return 'development'
}

const buildEnv = getAppEnv()

export const buildEnvironmentConfig: BuildEnvironmentConfig = {
  env: buildEnv,
  isDev: buildEnv === 'development',
  isQA: buildEnv === 'qa',
  isProd: buildEnv === 'production',
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 
    (buildEnv === 'production' 
      ? 'https://api.rentcafe.com' 
      : buildEnv === 'qa' 
        ? 'https://qa-api.rentcafe.com' 
        : 'http://localhost:5025'),
  enableDevtools: import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' || buildEnv !== 'production',
  logLevel: (import.meta.env.VITE_LOG_LEVEL as any) || 
    (buildEnv === 'development' ? 'debug' : buildEnv === 'qa' ? 'info' : 'error'),
  chunkSizeWarningLimit: parseInt(import.meta.env.VITE_CHUNK_SIZE_WARNING_LIMIT || '500', 10),
}

// Global environment constants (declared in vite.config.ts)
declare global {
  const __APP_ENV__: string
  const __DEV__: boolean
  const __QA__: boolean
  const __PROD__: boolean
}

// Console logging utility that respects environment
export const envLog = {
  debug: (...args: any[]) => {
    if (buildEnvironmentConfig.logLevel === 'debug') {
      console.debug('[DEBUG]', ...args)
    }
  },
  info: (...args: any[]) => {
    if (['debug', 'info'].includes(buildEnvironmentConfig.logLevel)) {
      console.info('[INFO]', ...args)
    }
  },
  warn: (...args: any[]) => {
    if (['debug', 'info', 'warn'].includes(buildEnvironmentConfig.logLevel)) {
      console.warn('[WARN]', ...args)
    }
  },
  error: (...args: any[]) => {
    console.error('[ERROR]', ...args)
  }
}


export default env; 